import { ChartOptions } from 'chart.js';
import { NumericsDataPoint } from './VitalDataTypes';

interface NumericsConfig {
  title: string;
  color: string;
  backgroundColor: string;
  tooltipLabel: (value: number | null) => string;
  yAxis?: {
    min?: number;
    max?: number;
    ticks?: {
      stepSize?: number;
    };
  };
}

// Common dataset options for all numerics
export const baseDatasetOptions = {
  pointRadius: 1,
  pointHoverRadius: 5,
  tension: 0.3,
  borderWidth: 2,
  spanGaps: false,
} as const;

// Common chart options for all numerics
export const baseChartOptions = {
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'hour',
        displayFormats: {
          hour: 'HH:mm'
        }
      },
      ticks: {
        maxTicksLimit: 12,
        color: '#6b7280'
      },
      grid: {
        display: true,
        drawOnChartArea: true,
        color: 'rgba(107, 114, 128, 0.1)'
      }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      mode: 'index' as const,
      intersect: false
    }
  },
  interaction: {
    mode: 'index' as const,
    intersect: false
  }
} as const;

// Configuration for each numerics
export const numericsConfigs: Record<string, NumericsConfig> = {
  heartRate: {
    title: 'Heart Rate (BPM)',
    color: '#FF6B6B',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    tooltipLabel: (value: number | null) => (value === 0 || value) ? `${value} BPM` : 'No data'
  },
  respiratoryRate: {
    title: 'Respiratory Rate (breaths/min)',
    color: '#4ECDC4',
    backgroundColor: 'rgba(78, 205, 196, 0.1)',
    tooltipLabel: (value: number | null) => (value === 0 || value) ? `${value} breaths/min` : 'No data'
  },
  temperature: {
    title: 'Temperature (°C)',
    color: '#FFB400',
    backgroundColor: 'rgba(255, 180, 0, 0.1)',
    tooltipLabel: (value: number | null) => (value === 0 || value) ? `${value}°C` : 'No data',
    yAxis: {
      min: 20,
      max: 41,
      ticks: {
        stepSize: 5
      }
    }
  },
  steps: {
    title: 'Step Count',
    color: '#45B7D1',
    backgroundColor: 'rgba(69, 183, 209, 0.1)',
    tooltipLabel: (value: number | null) => (value === 0 || value) ? `${value} steps` : 'No data'
  },
  eda: {
    title: 'Electrodermal Activity (μS)',
    color: '#96C93D',
    backgroundColor: 'rgba(150, 201, 61, 0.1)',
    tooltipLabel: (value: number | null) => (value === 0 || value) ? `${value} μS` : 'No data',
    yAxis: {
      min: 0,
      ticks: {
        stepSize: 2
      }
    }
  },
  prv: {
    title: 'Pulse Rate Variability (ms)',
    color: '#A18CD1',
    backgroundColor: 'rgba(161, 140, 209, 0.1)',
    tooltipLabel: (value: number | null) => (value === 0 || value) ? `${value} ms` : 'No data',
    yAxis: {
      min: 0,
      ticks: {
        stepSize: 10
      }
    }
  }
};

// Get chart options for a specific vital sign
export const getNumericsChartOptions = (vitalType: keyof typeof numericsConfigs, minX: number, maxX: number): ChartOptions<'line'> => {
  const config = numericsConfigs[vitalType];
  
  return {
    ...baseChartOptions,
    scales: {
      ...baseChartOptions.scales,
      x: {
        ...baseChartOptions.scales.x,
        min: minX,
        max: maxX,
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(107, 114, 128, 0.1)'
        },
        ticks: {
          color: '#6b7280',
          ...(config.yAxis?.ticks || {})
        },
        afterFit: (scale) => {
          scale.width = 60;
        },
        ...config.yAxis
      }
    },
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        ...baseChartOptions.plugins.tooltip,
        mode: 'nearest',
        callbacks: {
          label: (context: any) => config.tooltipLabel(context.raw.y),
          title: (tooltipItems: any[]) => {
            if (tooltipItems[0]) {
              const date = new Date(tooltipItems[0].parsed.x);
              return date.toLocaleTimeString();
            }
            return '';
          }
        }
      }
    },
    interaction: {
      ...baseChartOptions.interaction,
      mode: 'nearest'
    }
  };
};

export const getNumericsChartData = (vitalType: keyof typeof numericsConfigs, data: NumericsDataPoint<number>[]) => {
  const config = numericsConfigs[vitalType];
  return {
    datasets: [{
      label: config.title,
      data: data.map(record => ({
        x: new Date(record.timestamp).getTime(),
        y: record.value === 0 ? null : record.value,
      })),
      ...baseDatasetOptions,
      lineTension: 0,
      borderColor: config.color,
      spanGaps: false,
      fill: 'origin',
      segment: {
        borderColor: (ctx: any) => {
          if (!ctx?.p0?.raw?.y || !ctx?.p1?.raw?.y) {
            return 'rgba(107, 114, 128, 0.3)';
          }
          return config.color;
        }
      },
      backgroundColor: (ctx: any) => {
        if (!ctx?.raw) {
          return config.backgroundColor;
        }
        return ctx.raw.y === null ? 'rgba(107, 114, 128, 0.3)' : config.backgroundColor;
      }
    }]
  };
};

// Activity chart configurations and functions
export const activityConfig = {
  title: 'Activity Classification',
  color: 'rgba(75, 192, 192, 1)',
  backgroundColor: 'rgba(75, 192, 192, 0.1)',
  tooltipLabel: (value: number | string | null) => (value === 0 || value) ? `Activity: ${value}` : 'No data',
  yAxis: {
    type: 'category' as const,
    labels: ['still', 'walking', 'running'] as string[]
  }
} as const;

export const getActivityChartOptions = (minX: number, maxX: number): ChartOptions<'line'> => {
  return {
    ...baseChartOptions,
    scales: {
      ...baseChartOptions.scales,
      x: {
        ...baseChartOptions.scales.x,
        min: minX,
        max: maxX,
      },
      y: {
        type: 'category',
        labels: activityConfig.yAxis.labels,
        grid: {
          drawOnChartArea: true,
          color: 'rgba(107, 114, 128, 0.1)'
        },
        ticks: {
          color: '#6b7280'
        },
        afterFit: (scale) => {
          scale.width = 60;
        },
      }
    },
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        ...baseChartOptions.plugins.tooltip,
        mode: 'nearest',
        callbacks: {
          label: (context: any) => activityConfig.tooltipLabel(context.parsed.y),
          title: (tooltipItems: any[]) => {
            if (tooltipItems[0]) {
              const date = new Date(tooltipItems[0].parsed.x);
              return date.toLocaleTimeString();
            }
            return '';
          }
        }
      }
    },
    interaction: {
      ...baseChartOptions.interaction,
      mode: 'nearest'
    }
  };
};

export const getActivityChartData = (data: NumericsDataPoint<string>[]) => {
  return {
    datasets: [{
      label: activityConfig.title,
      data: data
        .filter(a => a.value !== 'generic' && a.value !== null && a.value !== '')
        .map(record => ({
          x: new Date(record.timestamp).getTime(),
          y: record.value
        })),
      ...baseDatasetOptions,
      showLine: false,
      lineTension: 0,           
      backgroundColor: activityConfig.color,
      borderColor: activityConfig.color,
      pointBackgroundColor: activityConfig.color,
      pointBorderColor: activityConfig.color
    }]
  };
};

// Waveform chart configurations and functions
export const waveformConfigs = {
  bvp: {
    title: 'Blood Volume Pulse (1 min)',
    color: 'rgba(255, 99, 132, 1)',
    tooltipLabel: (value: number) => `${value.toFixed(3)}`,
    yAxis: {
      title: {
        display: true,
        text: 'Amplitude'
      }
    }
  },
  eda: {
    title: 'Electrodermal Activity (1 min)',
    color: 'rgba(54, 162, 235, 1)',
    tooltipLabel: (value: number) => `${value.toFixed(3)} μS`,
    yAxis: {
      title: {
        display: true,
        text: 'Amplitude'
      }
    }
  }
} as const;

// Common dataset options for waveforms
export const baseWaveformDatasetOptions = {
  borderWidth: 1,
  pointRadius: 0,
  fill: false,
  tension: 0.4
} as const;

// Base options for waveform charts
export const baseWaveformChartOptions = {
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'linear' as const,
      display: false,
      grid: {
        display: false
      }
    }
  },
  plugins: {
    legend: { display: false }
  },
  animation: false
} as const;

// Get chart options for waveform
export const getWaveformChartOptions = (waveformType: keyof typeof waveformConfigs): ChartOptions<'line'> => {
  const config = waveformConfigs[waveformType];
  
  return {
    ...baseWaveformChartOptions,
    scales: {
      ...baseWaveformChartOptions.scales,
      y: {
        display: true,
        ...config.yAxis
      }
    },
    plugins: {
      ...baseWaveformChartOptions.plugins,
      tooltip: {
        mode: 'nearest',
        intersect: false,
        callbacks: {
          label: (context: any) => config.tooltipLabel(context.raw)
        }
      }
    }
  };
};

// Get chart data for waveform
export const getWaveformChartData = (
  waveformType: keyof typeof waveformConfigs, 
  data?: number[]
) => {
  // Return empty chart data if no data provided
  if (!data || !Array.isArray(data)) {
    return {
      labels: [],
      datasets: [{
        label: waveformConfigs[waveformType]?.title ?? 'No Data',
        data: [],
        ...baseWaveformDatasetOptions,
        borderColor: waveformConfigs[waveformType]?.color ?? '#cccccc'
      }]
    };
  }

  const config = waveformConfigs[waveformType];
  
  // Safely access config properties with fallbacks
  return {
    labels: data.map((_, i) => i),
    datasets: [{
      label: config?.title ?? 'Untitled',
      data: data,
      ...baseWaveformDatasetOptions,
      borderColor: config?.color ?? '#000000'
    }]
  };
};

export const getEQ5DScore = (dateToSurveyAvailability: any, dtStr?: string) => {
  if (dtStr && dateToSurveyAvailability[dtStr]) {
      const total = Number(dateToSurveyAvailability[dtStr].eq_1) + Number(dateToSurveyAvailability[dtStr].eq_2) + Number(dateToSurveyAvailability[dtStr].eq_3) + Number(dateToSurveyAvailability[dtStr].eq_4) + Number(dateToSurveyAvailability[dtStr].eq_5);
      return (total / 5).toFixed(2);
  } else {
      return "-"
  }
};
